import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSystem } from '@ohif/core';
import { authService } from '../auth/authService';
import { CRITICAL_RESULTS_REFRESH_RETRY_MS } from '../constants/criticalResultsDashboard.constants';
import facilityService from '../services/facilityService';
import type { Facility } from '../types/facility.types';
import { buildRefreshWarningMessage } from '../utils/criticalResultsRefreshUtils';
import { hasRole, ROLES } from '../utils/permissions';
import { useAutoRefreshScheduler, useRefreshSection } from './useCriticalResultsSectionRefresh';
import type { RefreshFetchResult, RefreshTrigger } from './useCriticalResultsSectionRefresh';

export function useCriticalResultsNotifyWarning() {
  const { servicesManager } = useSystem();
  const { uiNotificationService } = servicesManager.services;
  const scheduleRetryRef = useRef<() => void>(() => {});

  const notifyWarning = useCallback(
    (message: string) => {
      uiNotificationService?.show({
        title: 'Refresh warning',
        message,
        type: 'warning',
        duration: 5000,
      });
      scheduleRetryRef.current();
    },
    [uiNotificationService]
  );

  return { notifyWarning, scheduleRetryRef, uiNotificationService };
}

export function useCriticalResultsFacilities(
  isSuperAdmin: boolean,
  facilityId: number | null,
  updateFilters: (partial: { facilityId: number }, options?: { resetPage?: boolean }) => void
) {
  const [facilityOptions, setFacilityOptions] = useState<Facility[]>([]);

  useEffect(() => {
    if (!isSuperAdmin) {
      return;
    }
    facilityService.getAllFacilities(1).then(response => {
      if (response.success && response.data) {
        setFacilityOptions(response.data);
        if (!facilityId && response.data.length > 0) {
          updateFilters({ facilityId: Number(response.data[0].id) }, { resetPage: true });
        }
      }
    });
  }, [isSuperAdmin, facilityId, updateFilters]);

  const facilitySelectOptions = useMemo(
    () =>
      facilityOptions.map(facility => ({
        value: String(facility.id),
        label: facility.facility_name,
      })),
    [facilityOptions]
  );

  return { facilitySelectOptions };
}

export function useCriticalResultsAutoRefresh(
  enabled: boolean,
  intervalSec: number,
  onTick: (trigger: 'auto' | 'retry') => void,
  scheduleRetryRef: React.MutableRefObject<() => void>
) {
  const { scheduleRetry } = useAutoRefreshScheduler({
    enabled,
    intervalMs: intervalSec * 1000,
    retryDelayMs: CRITICAL_RESULTS_REFRESH_RETRY_MS,
    onTick,
  });

  useEffect(() => {
    scheduleRetryRef.current = scheduleRetry;
  }, [scheduleRetry, scheduleRetryRef]);

  return { scheduleRetry };
}

export function useCriticalResultsSection<T>(options: {
  enabled: boolean;
  fetcher: () => Promise<RefreshFetchResult<T>>;
  notifyWarning: (message: string) => void;
}) {
  return useRefreshSection<T>({
    enabled: options.enabled,
    fetcher: options.fetcher,
    onWarning: message => options.notifyWarning(message),
  });
}

export function useCriticalResultsDefaultFacilityId() {
  const user = authService.getUser();
  return useMemo(() => {
    const parsed = Number(authService.getFacilityId() || user?.facility_id);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }, [user]);
}

export function useCriticalResultsSuperAdmin() {
  const user = authService.getUser();
  return hasRole(user, ROLES.SUPER_ADMIN);
}

export function buildDepartmentOptions(
  departmentSummary: Array<{ department: string }> | undefined
) {
  const base = [{ value: '', label: 'All departments' }];
  const fromSummary = (departmentSummary || []).map(item => ({
    value: item.department,
    label: item.department,
  }));
  return [...base, ...fromSummary];
}

export function buildReferringClinicianOptions(
  referringClinicianSummary: Array<{ name: string }> | undefined,
  selectedValue = ''
) {
  const base = [{ value: '', label: 'All clinicians' }];
  const names = new Set(
    (referringClinicianSummary || [])
      .map(item => item.name?.trim())
      .filter((name): name is string => Boolean(name) && name.toUpperCase() !== 'N/A')
  );

  if (selectedValue.trim()) {
    names.add(selectedValue.trim());
  }

  const options = [...names]
    .sort((a, b) => a.localeCompare(b))
    .map(name => ({ value: name, label: name }));

  return [...base, ...options];
}

export function aggregateRefreshWarning(
  sections: Array<{ warning: string | null }>,
  prefix = 'Some sections could not be refreshed. Existing data is shown.'
) {
  return buildRefreshWarningMessage(
    sections.map(section => section.warning),
    prefix
  );
}

export type { RefreshTrigger };
